import React, { useEffect, useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { isEditableNumericCell } from '../modules/oneClickValueProcessor';
import { buildChartModel, buildTableTitle, getSeriesColor } from './DataTable/chartSeriesUtils';
import { useCellEditor } from './DataTable/useCellEditor';

const CheckboxLegend = ({ legendNames, selectedNames, onToggle, series }) => {
  // 渲染可勾选图例并支持颜色预览。
  if (!legendNames.length) {
    return null;
  }

  return (
    <div className="chart-checkbox-group">
      {legendNames.map((name) => (
        <label key={name} className="chart-checkbox-item">
          <input
            type="checkbox"
            checked={selectedNames.includes(name)}
            onChange={() => onToggle(name)}
          />
          <span>{name}</span>
          <span
            className="chart-checkbox-color"
            style={{ backgroundColor: getSeriesColor(series, legendNames, name) }}
          />
        </label>
      ))}
    </div>
  );
};

const buildLegendSelected = (legendNames, selectedNames) => Object.fromEntries(
  // 生成ECharts图例选中映射。
  legendNames.map((name) => [name, selectedNames.includes(name)])
);

const useLegendSelection = (legendNames) => {
  // 管理图例默认全选与勾选切换。
  const [selectedNames, setSelectedNames] = useState([]);

  useEffect(() => {
    // 修改为始终默认全选所有图例
    setSelectedNames(legendNames);
  }, [legendNames]);

  const toggleName = (name) => {
    setSelectedNames((prev) => (
      prev.includes(name) ? prev.filter((item) => item !== name) : [...prev, name]
    ));
  };

  return [selectedNames, toggleName];
};

const FrequencyChart = ({ tables, documentType, deviceType }) => {
  // 渲染频率曲线图表。
  const { chartTitle, gridLeft, xAxis, yAxis, seriesData } = useMemo(
    () => buildChartModel({ tables, documentType, deviceType }),
    [tables, documentType, deviceType]
  );

  const legendNames = useMemo(
    () => [...new Set(seriesData.map((series) => series.name).filter((name) => name?.trim()))],
    [seriesData]
  );
  const showLegend = legendNames.length > 1;
  const [selectedNames, toggleName] = useLegendSelection(legendNames);

  if (!tables?.length) {
    return (
      <div className="chart-container">
        <h4>数据可视化图表</h4>
        <div className="empty-chart">暂无可用数据</div>
      </div>
    );
  }

  return (
    <div className="chart-container">
      <h4>{chartTitle}</h4>
      <ReactECharts
        className="chart-embed"
        theme="light"
        option={{
          title: { text: chartTitle, left: 'center' },
          tooltip: { trigger: 'item' },
          ...(showLegend ? {
            legend: {
              show: false,
              data: legendNames,
              selected: buildLegendSelected(legendNames, selectedNames)
            }
          } : {}),
          grid: { left: gridLeft, right: '10%', bottom: legendNames.length ? '25%' : '20%', top: '20%' },
          xAxis,
          yAxis,
          series: seriesData
        }}
      />
      {showLegend && (
        <CheckboxLegend
          legendNames={legendNames}
          selectedNames={selectedNames}
          onToggle={toggleName}
          series={seriesData}
        />
      )}
    </div>
  );
};

export default function DataTable({ tables, documentType, deviceType, enableCellEdit = false, onCellValueChange }) {
  // 渲染表格数据与图表组合视图。
  const {
    editingCell,
    draftValue,
    setDraftValue,
    startEditing,
    commitEditing,
    resetEditor,
  } = useCellEditor(onCellValueChange);

  if (!tables?.length) {
    return null;
  }

  return (
    <div>
      {tables.map((table, index) => {
        const { columns = [], rows = [], metadata = {}, device_type = '' } = table || {};
        if (!Array.isArray(columns) || !Array.isArray(rows) || columns.length === 0 || rows.length === 0) {
          return null;
        }

        const visibleColumns = columns.filter((col) => col !== '删除');
        if (visibleColumns.length === 0) {
          return null;
        }

        return (
          <div key={`${device_type || 'table'}-${index}`} className="table-section">
            <h3>{buildTableTitle({ deviceType, device_type, metadata, index, documentType })}</h3>
            {enableCellEdit && <p className="editable-hint">提示：带 "可修改" 的列支持双击单元格修改数值。</p>}
            <table className="data-table">
              <thead>
                <tr>
                  {visibleColumns.map((col) => {
                    const hasEditableCell = enableCellEdit && rows.some((row) => isEditableNumericCell(col, row[col]));
                    return (
                      <th key={col} className={hasEditableCell ? 'editable-column-header' : ''}>
                        {col}{hasEditableCell ? ' -可修改' : ''}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, rowIndex) => (
                  <tr key={`${index}-${rowIndex}`}>
                    {visibleColumns.map((col) => {
                      const isEditable = enableCellEdit && isEditableNumericCell(col, row[col]);
                      const isCurrentEditing =
                        editingCell &&
                        editingCell.tableIndex === index &&
                        editingCell.rowIndex === rowIndex &&
                        editingCell.columnKey === col;
                      const displayValue = row[col] ?? '-';

                      return (
                        <td
                          key={`${rowIndex}-${col}`}
                          className={isEditable ? 'editable-cell' : ''}
                          title={isEditable ? `${displayValue}\n双击可编辑该数值` : String(displayValue)}
                          onDoubleClick={() => {
                            if (!isEditable) return;
                            startEditing(index, rowIndex, col, row[col]);
                          }}
                        >
                          {isCurrentEditing ? (
                            <input
                              className="cell-editor"
                              autoFocus
                              type="number"
                              step="0.01"
                              value={draftValue}
                              onChange={(e) => setDraftValue(e.target.value)}
                              onBlur={commitEditing}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  commitEditing();
                                }
                                if (e.key === 'Escape') {
                                  resetEditor();
                                }
                              }}
                            />
                          ) : (
                            <span className="cell-text-truncate">{displayValue}</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
      <FrequencyChart tables={tables} documentType={documentType} deviceType={deviceType} />
    </div>
  );
}